// HGS – HMSI Governance System — Mock Data
// Realistic sample data for demo purposes only

import { ORG_TREE, listDepartments, listDivisions } from './orgTree';

// DEPARTMENTS — kept as a flat list for backward-compat with legacy code that
// filters/groups by `department`. Each entry now also carries OH + Division
// lineage so consumers can do hierarchical filtering when needed.
export const DEPARTMENTS = listDepartments().map((d) => ({
  id: d.deptId,
  label: d.deptLabel,
  color: d.color,
  operatingHead: d.ohId,
  ohLabel: d.ohLabel,
  division: d.divId,
  divisionLabel: d.divLabel,
}));

export const ROLES = [
  { id: 'operating_head', label: 'Operating Head', color: '#CC0000', description: 'Owns the Risk Universe; final approver across all divisions in their domain' },
  { id: 'department_head', label: 'Department Head', color: '#1F2937', description: 'Executes PCC / Fraud / DLC for one department' },
  { id: 'division_head', label: 'Division Head', color: '#0EA5E9', description: 'Reviews and approves submissions across departments in their division' },
  { id: 'legal', label: 'Legal', color: '#7C3AED', description: 'Final consolidated view, evidence, audit trail' },
  { id: 'admin', label: 'GRC Secretariat', color: '#F59E0B', description: 'Risk Management & GRC Secretariat — owns governance framework, applicability mapping & consolidated review' },
];

// ---- DEMO USERS — derived from ORG_TREE ----
// Each user shape: { userId, email, name, role, operatingHead, division, department, avatar }

const _ohUsers = Object.values(ORG_TREE).map((oh) => ({
  userId: `u-oh-${oh.id}`,
  email: oh.head.email,
  name: oh.head.name,
  role: 'operating_head',
  operatingHead: oh.id,
  ohLabel: oh.label,
  division: null,
  department: null,
  avatar: oh.head.avatar,
}));

const _divUsers = listDivisions().map(({ ohId, ohLabel, divId, divLabel, head }) => ({
  userId: `u-div-${divId}`,
  email: head.email,
  name: head.name,
  role: 'division_head',
  operatingHead: ohId,
  ohLabel,
  division: divId,
  divisionLabel: divLabel,
  department: null,
  avatar: head.avatar,
}));

const _deptUsers = listDepartments().map((d) => ({
  userId: `u-dept-${d.deptId}`,
  email: d.deptHead.email,
  name: d.deptHead.name,
  role: 'department_head',
  operatingHead: d.ohId,
  ohLabel: d.ohLabel,
  division: d.divId,
  divisionLabel: d.divLabel,
  department: d.deptId,
  departmentLabel: d.deptLabel,
  avatar: d.deptHead.avatar,
}));

export const DEMO_USERS = [
  ..._ohUsers,
  ..._divUsers,
  ..._deptUsers,
  { userId: 'u-lg-1', email: 'legal@hmsi.com', name: 'Neha Gupta', role: 'legal', operatingHead: null, division: null, department: null, avatar: 'NG' },
  { userId: 'u-ad-1', email: 'admin@hmsi.com', name: 'Aakash Sharma', role: 'admin', operatingHead: null, division: null, department: null, avatar: 'AS' },
];

export const DASHBOARD_KPIS = {
  totalGovernanceItems: 248,
  overallCompletion: 76,
  highRiskItems: 14,
  pendingReviews: 23,
  pendingLegalClosure: 8,
  overdueActions: 11,
};

// 5x5 risk heatmap — Likelihood (rows) x Impact (cols) — counts of risks in each cell
export const RISK_HEATMAP = [
  [2, 1, 0, 0, 0], // Rare
  [3, 5, 2, 1, 0], // Unlikely
  [4, 8, 6, 3, 1], // Possible
  [1, 4, 7, 5, 2], // Likely
  [0, 1, 3, 4, 2], // Almost Certain
];
export const HEATMAP_LIKELIHOOD = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];
export const HEATMAP_IMPACT = ['Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic'];

export const MODULE_HEALTH = [
  { id: 'risk', name: 'Risk Assessment', completion: 82, items: 64, color: '#CC0000', status: 'on-track' },
  { id: 'pcc', name: 'Product Compliance Data', completion: 71, items: 88, color: '#1F2937', status: 'attention' },
  { id: 'fraud', name: 'Fraud Prevention', completion: 89, items: 42, color: '#0EA5E9', status: 'on-track' },
  { id: 'dlc', name: 'DLC', completion: 64, items: 54, color: '#7C3AED', status: 'attention' },
];

export const TOP_CRITICAL_RISKS = [
  {
    id: 'R-2025-001',
    description: 'Global raw material shortage may impact production continuity',
    category: 'Supply Chain',
    owner: 'Priya Verma',
    division: 'Manufacturing',
    score: 20,
    level: 'High',
    status: 'In Progress',
  },
  {
    id: 'R-2025-014',
    description: 'Regulatory shift in BS-VI Phase 2 emission norms',
    category: 'Regulatory',
    owner: 'Aakash Sharma',
    division: 'R&D',
    score: 18,
    level: 'High',
    status: 'Pending Review',
  },
  {
    id: 'R-2025-027',
    description: 'Cybersecurity threat — telematics platform vulnerability',
    category: 'IT & Cyber',
    owner: 'Rajesh Kumar',
    division: 'Digital',
    score: 16,
    level: 'High',
    status: 'Mitigation Drafted',
  },
  {
    id: 'R-2025-033',
    description: 'Supplier consolidation risk — Tier-2 vendor concentration',
    category: 'Supply Chain',
    owner: 'Priya Verma',
    division: 'Procurement',
    score: 15,
    level: 'High',
    status: 'In Progress',
  },
  {
    id: 'R-2025-045',
    description: 'EV transition — legacy ICE inventory obsolescence',
    category: 'Strategic',
    owner: 'Aakash Sharma',
    division: 'Strategy',
    score: 14,
    level: 'Medium',
    status: 'Pending Review',
  },
];

export const PENDING_ACTIONS = [
  { id: 'A-101', title: 'Approve alternate supplier list — Mg alloys', owner: 'Operating Head', dueDate: '2025-08-05', priority: 'High', daysLeft: 3 },
  { id: 'A-102', title: 'Sign-off DLC declaration — Q2 Manufacturing', owner: 'Division Head', dueDate: '2025-08-08', priority: 'High', daysLeft: 6 },
  { id: 'A-103', title: 'Legal closure — supplier MSA amendment', owner: 'Legal', dueDate: '2025-08-12', priority: 'Medium', daysLeft: 10 },
  { id: 'A-104', title: 'Upload evidence — purchase approval matrix', owner: 'Operating Head', dueDate: '2025-07-30', priority: 'High', daysLeft: -2 },
  { id: 'A-105', title: 'Review fraud control checklist — billing', owner: 'Operating Head', dueDate: '2025-08-15', priority: 'Medium', daysLeft: 13 },
];

export const RISK_CATEGORIES = [
  'Supply Chain',
  'Regulatory',
  'IT & Cyber',
  'Operational',
  'Strategic',
  'Financial',
  'Reputational',
  'People & Culture',
  'Environmental',
  'Quality',
];

export const SAMPLE_RISK = {
  id: 'R-2025-001',
  category: 'Supply Chain',
  description: 'Global raw material shortage may impact production continuity',
  currentControls: 'Multi-source vendor strategy in place; 60-day safety stock for critical alloys; quarterly supplier review.',
  frequency: 'Possible',
  businessImpact: 'Major',
  currentMeasuresLevel: 'Partial',
  degreeOfRisk: 'High',
  mitigationPlan: 'Onboard 2 alternate suppliers from Southeast Asia by Q3; increase safety stock to 90 days for top-5 critical raw materials; invest in supplier development program.',
  owner: 'Priya Verma',
  ownerRole: 'Operating Head',
  targetCompletionDate: '2025-12-31',
  status: 'In Progress',
};

export const AI_COPILOT_RESPONSES = {
  explain: {
    title: 'Risk Explanation',
    summary: 'Global raw material shortages are driven by post-pandemic supply chain reconfiguration, geopolitical tensions affecting key mineral exports (steel, aluminium, rare earths), and accelerating EV demand competing for the same inputs. For HMSI, this directly threatens production continuity and cost stability.',
    drivers: [
      'Geopolitical: Restrictions on rare-earth exports from key markets',
      'Demand-side: Global EV growth creating Mg/Al/Li shortages',
      'Logistics: Container availability and Red Sea shipping disruptions',
      'Supplier concentration: ~40% of critical alloys from a single region',
    ],
    confidence: 'High',
  },
  examples: {
    title: 'Comparable Industry Cases',
    cases: [
      { company: 'Global OEM A', year: 2023, impact: '14-day production halt due to wire harness shortage; ₹820 Cr revenue loss equivalent.' },
      { company: 'Global OEM B', year: 2024, impact: 'Switched to dual-sourcing for semiconductors after 9-month shortage; cycle time +18 days.' },
      { company: 'Tier-1 Supplier C', year: 2024, impact: 'Aluminium alloy price volatility caused 7% margin erosion; activated long-term hedging.' },
    ],
  },
  draft: {
    title: 'Suggested Risk Description (Draft)',
    text: 'Persistent disruption in global raw material supply chains — particularly steel, aluminium alloys, and electronic components — poses a significant risk to HMSI\'s production continuity, lead times, and unit economics. Vulnerability is amplified by single-region supplier concentration and geopolitical export restrictions on rare-earth materials.',
  },
  score: {
    title: 'Suggested Risk Score',
    likelihood: 'Possible (3/5)',
    impact: 'Major (4/5)',
    score: 12,
    band: 'High',
    rationale: 'Three peer OEMs reported material-driven production delays in the last 18 months. HMSI safety stock of 60 days mitigates short-term shock but leaves exposure to >2-month disruptions.',
  },
  mitigation: {
    title: 'Suggested Mitigation Plan',
    actions: [
      { action: 'Dual-source 100% of Tier-1 critical materials by Q4 2025', owner: 'Procurement Head', due: '2025-12-31' },
      { action: 'Increase safety stock to 90 days for top-10 critical inputs', owner: 'Operating Head', due: '2025-09-30' },
      { action: 'Establish 2 strategic supplier partnerships in SE Asia', owner: 'Procurement Head', due: '2025-11-30' },
      { action: 'Implement real-time supplier risk monitoring dashboard', owner: 'IT + Procurement', due: '2025-10-15' },
      { action: 'Negotiate long-term price-lock contracts for top-3 alloys', owner: 'Finance + Procurement', due: '2025-12-15' },
    ],
  },
};

export const LINKAGE_DATA = {
  risk: {
    id: 'R-2025-001',
    name: 'Global raw material shortage may impact production continuity',
    category: 'Supply Chain',
    score: 'High',
  },
  links: [
    {
      module: 'Product Compliance Data',
      moduleKey: 'pcc',
      item: 'Alternate material approval process',
      description: 'Verify alternate Mg alloys meet IS/EU homologation specs',
      status: 'Amber',
      progress: 60,
      owner: 'R&D Compliance',
    },
    {
      module: 'Fraud Prevention',
      moduleKey: 'fraud',
      item: 'Supplier approval & procurement controls',
      description: 'Onboarding due-diligence + 4-eyes purchase approval enforced',
      status: 'Green',
      progress: 92,
      owner: 'Procurement Head',
    },
    {
      module: 'DLC',
      moduleKey: 'dlc',
      item: 'Division Head declaration on mitigation readiness',
      description: 'Quarterly self-verification by Manufacturing Division Head',
      status: 'Pending',
      progress: 0,
      owner: 'Rajesh Kumar',
    },
  ],
};

export const PCC_SECTIONS = [
  {
    id: 'material',
    name: 'Material Compliance',
    completion: 75,
    questions: [
      { id: 'm1', text: 'Are all raw materials sourced from RoHS-compliant suppliers?', answer: 'Yes', evidence: 'rohs_certificates_q2.pdf', remarks: 'All Tier-1 suppliers re-certified in May 2025.', status: 'Green' },
      { id: 'm2', text: 'Are alternate materials evaluated against original specifications?', answer: 'Yes', evidence: 'spec_comparison_matrix.xlsx', remarks: 'Reviewed by R&D head.', status: 'Green' },
      { id: 'm3', text: 'Has each new alloy passed homologation testing?', answer: 'No', evidence: null, remarks: 'Mg-Al alternate pending NABL test results.', status: 'Amber' },
      { id: 'm4', text: 'Is the material declaration system updated quarterly?', answer: 'N/A', evidence: null, remarks: 'Vendor declaration system being migrated.', status: 'Amber' },
    ],
  },
  {
    id: 'product',
    name: 'Product Specification',
    completion: 88,
    questions: [
      { id: 'p1', text: 'Are all product BOMs version-controlled?', answer: 'Yes', evidence: 'bom_audit_log.pdf', remarks: '', status: 'Green' },
      { id: 'p2', text: 'Are engineering change requests approved per SOP?', answer: 'Yes', evidence: 'ecr_log.pdf', remarks: '', status: 'Green' },
    ],
  },
  {
    id: 'labeling',
    name: 'Labeling & Packaging',
    completion: 60,
    questions: [
      { id: 'l1', text: 'Do all SKUs carry mandatory regulatory markings?', answer: 'Yes', evidence: 'label_audit.pdf', remarks: '', status: 'Green' },
      { id: 'l2', text: 'Is multilingual labeling for export SKUs verified?', answer: 'No', evidence: null, remarks: 'Vietnamese & Thai labels pending review.', status: 'Red' },
    ],
  },
];

export const FRAUD_CONTROLS = [
  {
    id: 'fc1',
    name: 'Supplier onboarding control',
    description: 'KYC + financial due-diligence + sanctions screening for every new supplier',
    answer: 'Yes',
    evidence: 'supplier_onboarding_log_2025.pdf',
    remarks: 'All new suppliers in 2025 cleared via standard process.',
    status: 'Green',
    lastVerified: '2025-07-12',
  },
  {
    id: 'fc2',
    name: 'Purchase approval control',
    description: 'Four-eyes principle for purchase orders > ₹10 Lakh; segregation of duties enforced',
    answer: 'Yes',
    evidence: 'po_approval_matrix.pdf',
    remarks: '',
    status: 'Green',
    lastVerified: '2025-07-20',
  },
  {
    id: 'fc3',
    name: 'Billing verification control',
    description: '3-way match (PO + GRN + Invoice) before payment release',
    answer: 'Yes',
    evidence: 'billing_audit_jun25.pdf',
    remarks: '2 exceptions reviewed and resolved.',
    status: 'Amber',
    lastVerified: '2025-07-18',
  },
  {
    id: 'fc4',
    name: 'Petty cash & expense control',
    description: 'Receipt-based reimbursement; spot audit twice a quarter',
    answer: 'Yes',
    evidence: 'expense_audit_q2.pdf',
    remarks: '',
    status: 'Green',
    lastVerified: '2025-07-05',
  },
  {
    id: 'fc5',
    name: 'Conflict-of-interest declarations',
    description: 'Annual COI declaration from all decision-makers in Procurement',
    answer: 'No',
    evidence: null,
    remarks: 'Pending refresh — collection deadline 31-Aug-2025.',
    status: 'Red',
    lastVerified: '2024-08-30',
  },
];

export const DLC_INSIGHTS = {
  unresolvedRisks: [
    { id: 'R-2025-001', title: 'Raw material shortage', score: 'High', age: 14 },
    { id: 'R-2025-027', title: 'Cybersecurity — telematics', score: 'High', age: 28 },
  ],
  pccPullthrough: [
    { item: 'Alternate material homologation', status: 'Amber', section: 'Material Compliance' },
    { item: 'Multilingual labeling for export SKUs', status: 'Red', section: 'Labeling & Packaging' },
  ],
  fraudPullthrough: [
    { item: 'Conflict-of-interest declarations', status: 'Red' },
    { item: 'Billing verification — 2 exceptions', status: 'Amber' },
  ],
};

export const APPROVAL_QUEUE = [
  {
    id: 'AP-201',
    type: 'Risk Assessment',
    title: 'Global raw material shortage — mitigation plan',
    submittedBy: 'Priya Verma',
    submittedOn: '2025-07-22',
    status: 'Pending',
    priority: 'High',
    comments: [],
  },
  {
    id: 'AP-202',
    type: 'PCC Section',
    title: 'Material Compliance — Q2 sign-off',
    submittedBy: 'R&D Compliance',
    submittedOn: '2025-07-20',
    status: 'Pending',
    priority: 'Medium',
    comments: [{ author: 'Operating Head', text: 'Need NABL test report before sign-off.', date: '2025-07-21' }],
  },
  {
    id: 'AP-203',
    type: 'Fraud Control',
    title: 'COI declaration refresh exemption request',
    submittedBy: 'Procurement',
    submittedOn: '2025-07-18',
    status: 'Approved',
    priority: 'High',
    comments: [{ author: 'Legal', text: 'Approved with extension till 31-Aug-2025.', date: '2025-07-19' }],
  },
  {
    id: 'AP-204',
    type: 'DLC Declaration',
    title: 'Manufacturing Division — Q2 self-verification',
    submittedBy: 'Rajesh Kumar',
    submittedOn: '2025-07-15',
    status: 'Rejected',
    priority: 'High',
    comments: [{ author: 'Operating Head', text: 'Re-submit with countermeasure for unresolved risk R-2025-001.', date: '2025-07-16' }],
  },
];

export const ADMIN_USERS = [
  { id: 'u1', name: 'Aakash Sharma', email: 'admin@hmsi.com', role: 'Admin', division: 'Corporate', status: 'Active' },
  { id: 'u2', name: 'Priya Verma', email: 'op.head@hmsi.com', role: 'Operating Head', division: 'Manufacturing', status: 'Active' },
  { id: 'u3', name: 'Rajesh Kumar', email: 'div.head@hmsi.com', role: 'Division Head', division: 'Manufacturing', status: 'Active' },
  { id: 'u4', name: 'Neha Gupta', email: 'legal@hmsi.com', role: 'Legal', division: 'Legal', status: 'Active' },
  { id: 'u5', name: 'Vivek Iyer', email: 'vivek.iyer@hmsi.com', role: 'Operating Head', division: 'R&D', status: 'Active' },
  { id: 'u6', name: 'Sunita Rao', email: 'sunita.rao@hmsi.com', role: 'Division Head', division: 'Procurement', status: 'Inactive' },
];

export const ADMIN_MODULES = [
  { id: 'mod1', name: 'Risk Assessment', enabled: true, owner: 'Operating Head', items: 64 },
  { id: 'mod2', name: 'Product Compliance Data', enabled: true, owner: 'R&D + Quality', items: 88 },
  { id: 'mod3', name: 'Fraud Prevention', enabled: true, owner: 'Internal Audit', items: 42 },
  { id: 'mod4', name: 'DLC Self-Verification', enabled: true, owner: 'Division Heads', items: 54 },
  { id: 'mod5', name: 'ESG & Sustainability', enabled: false, owner: 'CSO', items: 0 },
];

export const ADMIN_CHECKLIST_MASTER = [
  { id: 'cl1', module: 'PCC', section: 'Material Compliance', count: 12, lastUpdated: '2025-07-10' },
  { id: 'cl2', module: 'PCC', section: 'Product Specification', count: 8, lastUpdated: '2025-06-22' },
  { id: 'cl3', module: 'Fraud', section: 'Procurement Controls', count: 7, lastUpdated: '2025-07-15' },
  { id: 'cl4', module: 'Fraud', section: 'Financial Controls', count: 9, lastUpdated: '2025-07-18' },
  { id: 'cl5', module: 'DLC', section: 'Quarterly Declaration', count: 6, lastUpdated: '2025-07-01' },
];
